import app from 'ampersand-app'
import Fuse from 'fuse.js'
import keyBy from 'lodash/keyBy'
import { memo } from 'preact/compat'
import { useEffect, useMemo, useState } from 'preact/hooks'
import { useLocalStorage, useMedia } from 'react-use'
import useSWR from 'swr'
import { logger } from '../../utils'
import {
  bulkCreateTransactions,
  useCurrentEventSlug,
  useLocations,
  useTransactions
} from '../hooks'
import { TextButton } from '../text-button/text-button'
import type { Location, Product } from '../types'
import * as styles from './dispatch-form.module.css'
import { DispatchFormTitle, useDispatchFormState } from './dispatch-form-shared'
import { ProductTileMemo } from './product-tile'
import { AccessibleToggleGroup } from './toggle-group'
import type { useTabletMode } from './toggle-tablet-mode'

const log = logger.extend('dispatch-form')

const SONSTIGE_CATEGORY = 'Sonstige' as const

const DispatchRouteMemo = memo(function DispatchRoute({
  sourceId,
  setSourceId,
  targetId,
  setTargetId,
  storage,
  bars
}: {
  sourceId: string
  setSourceId: (sourceId: string) => void
  targetId: string
  setTargetId: (targetId: string) => void
  storage?: Location[]
  bars?: Location[]
}) {
  return (
    <div class={styles.route}>
      <div>
        <label for='source'>Von</label>
        <select
          class='pure-input-1'
          name='source'
          value={sourceId}
          onChange={(event) => setSourceId(event.currentTarget.value)}
          required
        >
          {storage && (
            <optgroup label='Lager'>
              {storage.map((store) => (
                <option key={store.id} value={store.id}>
                  {store.name}
                </option>
              ))}
            </optgroup>
          )}
          {bars && (
            <optgroup label='Bars'>
              {bars.map((bar) => (
                <option key={bar.id} value={bar.id}>
                  {bar.name}
                </option>
              ))}
            </optgroup>
          )}
        </select>
      </div>

      <div>
        <label for='target'>Nach</label>
        <select
          class='pure-input-1'
          name='target'
          value={targetId}
          onChange={(event) => setTargetId(event.currentTarget.value)}
          required
        >
          <option disabled value=''>
            Ziel wählen...
          </option>
          {bars && (
            <optgroup label='Bars'>
              {bars.map((bar) => (
                <option key={bar.id} value={bar.id}>
                  {bar.name}
                </option>
              ))}
            </optgroup>
          )}
          {storage && (
            <optgroup label='Lager'>
              {storage.map((store) => (
                <option key={store.id} value={store.id}>
                  {store.name}
                </option>
              ))}
            </optgroup>
          )}
        </select>
      </div>
    </div>
  )
})

function SubmitButton({
  disabled,
  itemsCount
}: {
  disabled: boolean
  itemsCount: number
}) {
  return (
    <button
      class='pure-button pure-button-primary'
      type='submit'
      disabled={disabled}
    >
      {itemsCount === 0 && 'Abschicken'}
      {itemsCount === 1 && `${itemsCount} Produkt dispatchen`}
      {itemsCount > 1 && `${itemsCount} Produkte dispatchen`}
    </button>
  )
}

interface DispatchFormTabletProps {
  onClose: () => void
  isTabletMode: boolean
  setIsTabletMode: ReturnType<typeof useTabletMode>[1]
}

type SortOrder = 'sortPosition' | 'name'

export function DispatchFormTablet({
  onClose,
  isTabletMode,
  setIsTabletMode
}: DispatchFormTabletProps) {
  const eventSlug = useCurrentEventSlug()
  const { mutate: mutateTransactions } = useTransactions()

  const { locations, storage, bars } = useLocations(eventSlug)
  const { data: products } = useSWR<Product[]>(
    `/api/events/${eventSlug}/products`
  )
  const productsById = keyBy(products || [], 'id')

  const {
    sourceId,
    setSourceId,
    targetId,
    setTargetId,
    comment,
    setComment,
    transactions,
    handleUpdateTransactions,
    resetFormState,
    clearPersistedState
  } = useDispatchFormState()
  const [sortOrder = 'sortPosition', setSortOrder] = useLocalStorage<SortOrder>(
    'dispatch-form-sort-order',
    'sortPosition'
  )
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const isBigScreen = useMedia('(min-width: 1024px)')
  const [showComment, setShowComment] = useState(isBigScreen)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // set source for user's with assigned location
  useEffect(() => {
    if (!sourceId && storage && locations) {
      const source = app.me.defaultSourceLocationId
        ? locations.find((store) => store.id === app.me.defaultSourceLocationId)
        : storage[0]
      if (source) {
        setSourceId(source.id)
        log(`set initial source: ${source.name} (${source.id})`)
      }
    }
  }, [locations, storage, sourceId, setSourceId])

  // set target for user's with assigned location (e.g. bar bosses)
  useEffect(() => {
    const locationId =
      app.me.defaultTargetLocationId || app.me.assignedLocationId
    if (!targetId && locations && locationId) {
      const target = locations.find((l) => l.id === locationId)
      if (target) {
        setTargetId(target.id)
        log(`set initial target: ${target.name} (${target.id})`)
      }
    }
  }, [locations, targetId, setTargetId])

  const [query, setQuery] = useState('')
  const fuse = useMemo(
    () =>
      new Fuse(products ?? [], {
        keys: ['name'],
        minMatchCharLength: 2,
        threshold: 0.25
      }),
    [products]
  )

  const allCategories = useMemo(() => {
    return Array.from(
      new Set(
        products
          ?.map((product) => product.categoryName)
          .filter((category) => category !== null)
      )
    ).sort((a, b) => a.localeCompare(b))
  }, [products])

  const filteredAndSortedProducts = useMemo(() => {
    let filteredProducts =
      query.length < 2
        ? products
        : fuse.search(query).map((result) => result.item)

    if (!filteredProducts) return null

    if (categoryFilter !== 'all') {
      filteredProducts = filteredProducts.filter((product) => {
        if (categoryFilter === SONSTIGE_CATEGORY) {
          return product.categoryName === null
        }
        return product.categoryName === categoryFilter
      })
    }

    // Sort products first
    const sortedProducts =
      sortOrder === 'name'
        ? filteredProducts.toSorted((a, b) => a.name.localeCompare(b.name))
        : filteredProducts

    // Group products by category
    const groupedProducts = sortedProducts.reduce(
      (groups, product) => {
        const category = product.categoryName || SONSTIGE_CATEGORY
        if (!groups[category]) {
          groups[category] = []
        }
        groups[category].push(product)
        return groups
      },
      {} as Record<string, Product[]>
    )

    // Sort categories alphabetically, with "Sonstige" always last
    const sortedGroupedProducts = Object.entries(groupedProducts).sort(
      (a, b) => {
        if (a[0] === SONSTIGE_CATEGORY) return 1
        if (b[0] === SONSTIGE_CATEGORY) return -1
        return a[0].localeCompare(b[0])
      }
    )

    return sortedGroupedProducts
  }, [products, sortOrder, query, fuse, categoryFilter])

  const isValid = Boolean(
    sourceId &&
      targetId &&
      Object.values(transactions).some(
        (tx) => tx.productId && (tx.inputPackages > 0 || tx.inputUnits > 0)
      )
  )

  if (!filteredAndSortedProducts) {
    return null
  }

  return (
    <form
      class={`pure-form ${styles.formTablet}`}
      onSubmit={async (event) => {
        event.preventDefault()

        setIsSubmitting(true)
        const payload = Object.values(transactions)
          .filter(
            (tx) => tx.productId && (tx.inputPackages > 0 || tx.inputUnits > 0)
          )
          .map((tx) => ({
            sourceId,
            targetId,
            productId: tx.productId,
            units:
              tx.inputPackages * productsById[tx.productId].packageSize +
              tx.inputUnits,
            comment
          }))

        try {
          await bulkCreateTransactions(eventSlug, payload)
          await mutateTransactions()
          clearPersistedState()
          onClose()
        } catch (error) {
          console.error(error)
        } finally {
          setIsSubmitting(false)
        }
      }}
    >
      <DispatchFormTitle
        targetName={locations?.find((l) => l.id === targetId)?.name}
        isTabletMode={isTabletMode}
        setIsTabletMode={setIsTabletMode}
        onClose={onClose}
      />

      <div class={styles.formTabletContent}>
        <section aria-label='Produkte' data-slot='products'>
          <header class={styles.productsHeader}>
            <div class='input-addon'>
              <i class='icon ion-ios-search-strong input-addon-item' />

              <input
                className={styles.searchInput}
                type='text'
                name='product-search'
                autoComplete='off'
                autoCorrect='off'
                autoCapitalize='off'
                enterKeyHint='search'
                placeholder='Produkt suchen...'
                value={query}
                onKeyDown={(event) => {
                  if (event.key === 'Escape' && query.length > 0) {
                    event.stopPropagation()
                    setQuery('')
                  }
                }}
                onChange={(event) => setQuery(event.currentTarget.value)}
              />

              <button
                type='button'
                class={styles.resetSearchButton}
                onClick={() => setQuery('')}
              >
                <i class='icon ion-close' />
              </button>
            </div>
            <AccessibleToggleGroup<SortOrder>
              label='Sortieren:'
              options={[
                { value: 'sortPosition', label: 'Manuell' },
                { value: 'name', label: 'A-Z' }
              ]}
              value={sortOrder}
              onChange={setSortOrder}
            />
            {allCategories.length > 0 && (
              <div class={styles.categoryFilter}>
                <AccessibleToggleGroup
                  options={[
                    { value: 'all', label: 'Alle' },
                    ...allCategories.map((category) => ({
                      value: category,
                      label: category
                    })),
                    { value: SONSTIGE_CATEGORY, label: 'Sonstige' }
                  ]}
                  value={categoryFilter}
                  onChange={setCategoryFilter}
                />
              </div>
            )}
          </header>

          <div class={styles.products}>
            {filteredAndSortedProducts.map(([category, categoryProducts]) => (
              <div key={category} class={styles.categoryGroup}>
                <h3 class={styles.categoryHeadline}>{category}</h3>

                <div class={styles.categoryProducts}>
                  {categoryProducts.map((product) => (
                    <ProductTileMemo
                      key={product.id}
                      transaction={transactions[product.id]}
                      product={product}
                      onUpdate={handleUpdateTransactions}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section aria-label='Route' data-slot='route'>
          <DispatchRouteMemo
            sourceId={sourceId}
            setSourceId={setSourceId}
            targetId={targetId}
            setTargetId={setTargetId}
            storage={storage}
            bars={bars}
          />
        </section>

        <section
          aria-label='Abschließen'
          class={styles.tabletFormSubmitSection}
        >
          {(showComment || isBigScreen) && (
            <div style={{ alignSelf: 'stretch' }}>
              <label htmlFor='comment'>Kommentar</label>
              <textarea
                name='comment'
                rows={3}
                style={{ width: '100%' }}
                value={comment}
                onChange={(event) => setComment(event.currentTarget.value)}
              />
            </div>
          )}

          <div class={styles.tabletFormSubmitButtons}>
            {!isBigScreen && (
              <TextButton onClick={() => setShowComment(!showComment)}>
                {showComment ? 'Kommentar ausblenden' : 'Kommentar einblenden'}
              </TextButton>
            )}
            <button
              type='button'
              class='pure-button'
              disabled={isSubmitting}
              onClick={() => {
                resetFormState()
              }}
            >
              Zurücksetzen
            </button>

            <SubmitButton
              disabled={!isValid || isSubmitting}
              itemsCount={Object.keys(transactions).length}
            />
          </div>
        </section>
      </div>
    </form>
  )
}
